import { useEffect, useRef, useState } from "react";

/** Default idle window before an in-progress edit auto-commits. */
export const DRAFT_COMMIT_DEBOUNCE_MS = 500;

/**
 * `useDraftText` — draft-buffered binding for a client-optimistic free-text leaf. Without it
 * **typing drops characters**: a plain controlled input re-renders from the remote-reconciled
 * snapshot on every keystroke's own echo, so under real latency an early keystroke's
 * `*_change` broadcast arrives while later keystrokes are still unacked and clobbers them.
 *
 * The fix is **uncontrolled while focused**: the input shows a local `draft`, not the
 * reconciled `value`, for as long as it's focused, so a same-field echo mid-burst never
 * rewinds the caret. `onCommit` fires the actual command (e.g. `set_pilot_text`) on a short
 * idle debounce **and** immediately on blur (whichever comes first for a given edit — the
 * debounce timer is cleared once either fires, so a single edit never double-commits). A
 * commit is skipped if the value is unchanged from the last-known committed/reconciled value,
 * so a focus-then-blur with no edit (or a debounce firing after a blur already flushed it)
 * doesn't emit a redundant no-op command that would otherwise pollute the undo stack.
 *
 * While **not** focused, the draft tracks the incoming reconciled `value` (a `useEffect` on
 * `value`), so a remote edit from another client still live-updates the field; the guard is
 * only up while the user is actively in the field. On focus the draft is already caught up
 * (it was tracking `value` the whole time it was unfocused), so there's nothing extra to seed.
 *
 * Kept framework-thin. Its callers are the sheets' EDIT inputs (`MechSheetEdit.tsx`,
 * `PilotSheetEdit.tsx`) and their NOTES (`SheetNotes`).
 */
export function useDraftText(
  value: string,
  onCommit: (value: string) => void,
  debounceMs: number = DRAFT_COMMIT_DEBOUNCE_MS,
): {
  value: string;
  onChange: (next: string) => void;
  onFocus: () => void;
  onBlur: () => void;
} {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKnown = useRef(value);

  // Not focused: keep the draft caught up with the reconciled snapshot (remote edits show).
  // While focused this is a no-op guard — the draft is locally owned until blur/unmount.
  useEffect(() => {
    if (!focused.current) {
      setDraft(value);
      lastKnown.current = value;
    }
  }, [value]);

  // Cancel any pending debounce on unmount so a stray commit never fires after the input
  // (and its owning dispatch/actor context) is gone.
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  function flush(next: string) {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (next !== lastKnown.current) {
      lastKnown.current = next;
      onCommit(next);
    }
  }

  return {
    value: draft,
    onFocus: () => {
      focused.current = true;
    },
    onChange: (next: string) => {
      setDraft(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => flush(next), debounceMs);
    },
    onBlur: () => {
      focused.current = false;
      flush(draft);
    },
  };
}
